import { useMemo, useRef, createRef } from 'react';
import gsap from 'gsap';
import { Container, useUi } from '@weareconceptstudio/core';
import Item from '../Item';

const GRID_COLUMNS = 3;

const getNeighborIndexes = (index, total, columns) => {
	const neighbors = new Set();

	const row = Math.floor(index / columns);
	const col = index % columns;

	for (let r = row - 2; r <= row + 2; r++) {
		for (let c = col - 2; c <= col + 2; c++) {
			if (r < 0 || c < 0 || c >= columns) continue;

			const neighborIndex = r * columns + c;
			if (neighborIndex === index || neighborIndex >= total) continue;

			neighbors.add(neighborIndex);
		}
	}

	return [...neighbors];
};

const Items = ({ list, containerRef, percent, className = '' }) => {
	const { winWidth } = useUi();
	const isDesktopScreen = useMemo(() => winWidth >= 1280, [winWidth]);

	const blockRefs = useRef([]);

	useMemo(() => {
		blockRefs.current = list.map((_, i) => blockRefs.current[i] ?? createRef());
	}, [list]);

	const animateItem = (index, active = false) => {
		const el = blockRefs.current[index]?.current;
		if (!el) return;

		el.dataset.reset = 'false';

		const containerBlock = getComputedStyle(containerRef.current);
		const scaleX = containerBlock.getPropertyValue('--scaleValueX');
		const scaleY = containerBlock.getPropertyValue('--scaleValueY');

		const beforeLayer = el.querySelector('.before-layer');
		const afterLayer = el.querySelector('.after-layer');
		const featuredTitle = el.querySelector('.featured-title');
		const featuredLocation = el.querySelector('.featured-location');
		const imageCont = el.querySelector('.image-cont');
		const elementLineBlock = el.querySelector('.element-link-block');

		const all = [el, beforeLayer, afterLayer, featuredTitle, featuredLocation, imageCont, elementLineBlock];
		gsap.killTweensOf(all);

		gsap.set(el, {
			zIndex: active ? 6 : 5,
		});

		if (active) {
			gsap.to(beforeLayer, { opacity: 1, duration: 0.7, ease: 'power4.out' });
			gsap.to(afterLayer, { opacity: 0, duration: 0.7, ease: 'power4.out' });
			gsap.to(featuredTitle, { y: 0, duration: 0.5, ease: 'power3.out' });
			gsap.to(featuredLocation, { opacity: 1, duration: 0.5, ease: 'power3.out' });
			gsap.to(imageCont, { scale: 1.05, duration: 0.5, ease: 'power3.out' });
			gsap.to(elementLineBlock, {
				scaleX: scaleX,
				scaleY: scaleY,
				duration: 0.5,
				ease: 'power3.out',
			});
		} else {
			gsap.to(beforeLayer, { opacity: 0, duration: 0.7, ease: 'power4.out' });
			gsap.to(afterLayer, { opacity: 1, duration: 0.7, ease: 'power4.out' });
			gsap.to(featuredTitle, { y: '100%', duration: 0.5, ease: 'power3.out' });
			gsap.to(featuredLocation, { opacity: 0, duration: 0.5, ease: 'power3.out' });
			gsap.to(imageCont, { scale: 1, duration: 0.5, ease: 'power3.out' });
			gsap.to(elementLineBlock, { scaleX: 1, scaleY: 1, duration: 0.5, ease: 'power3.out' });
		}
	};

	const handleMouseEnter = (i) => {
		if (!isDesktopScreen) return;

		const neighbors = getNeighborIndexes(i, list.length, GRID_COLUMNS);

		animateItem(i, true);
		neighbors.forEach((n) => animateItem(n, false));
	};

	const handleMouseLeave = () => {
		if (!isDesktopScreen) return;

		blockRefs.current.forEach((ref) => {
			const el = ref?.current;

			if (!el) return;

			if (el.dataset.reset === 'true') return;

			const beforeLayer = el.querySelector('.before-layer');
			const afterLayer = el.querySelector('.after-layer');
			const featuredTitle = el.querySelector('.featured-title');
			const featuredLocation = el.querySelector('.featured-location');
			const imageCont = el.querySelector('.image-cont');
			const elementLineBlock = el.querySelector('.element-link-block');

			gsap.killTweensOf([
				el,
				beforeLayer,
				afterLayer,
				featuredTitle,
				featuredLocation,
				imageCont,
				elementLineBlock,
			]);

			gsap.set(el, { zIndex: 1 });
			gsap.to(featuredTitle, { y: '100%', duration: 0.5, ease: 'power3.out' });
			gsap.to(featuredLocation, { opacity: 0, duration: 0.5, ease: 'power3.out' });
			gsap.to([beforeLayer, afterLayer], { opacity: 0, duration: 0.7, ease: 'power4.out' });
			gsap.to(imageCont, { scale: 1.05, duration: 0.5, ease: 'power3.out' });
			gsap.to(elementLineBlock, { scaleX: 1, scaleY: 1, duration: 0.5, ease: 'power3.out' });

			el.dataset.reset = 'true';
		});
	};

	return (
		<Container>
			<div className={`infinite-inner-wrapper initial-wrapper ${className}`}>
				{list.map((item, i) => (
					<Item
						key={`${item.slug}-${i}`}
						ref={blockRefs.current[i]}
						item={item}
						index={i}
						percent={percent}
						handleMouseEnter={() => handleMouseEnter(i)}
						handleMouseLeave={handleMouseLeave}
					/>
				))}
			</div>
		</Container>
	);
};

export default Items;
